import { Injectable } from '@angular/core';
import { SpacesClientService } from '@mode/shared/data-access-webapp';
import { CollectionAccessLevel } from '@mode/shared/contract-common';
import { SpaceDomain } from '@mode/shared/contract-common';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, mergeMap, switchMap, tap } from 'rxjs/operators';
import { BugsnagService } from '@mode/shared/util-error-handling';

export interface SearchQueryParams {
  orgName: string;
  spaceId: string;
  query: string;
}

@Injectable({
  providedIn: 'root',
})
export class CollectionAddMemberFacade {
  protected selectedResultsStream = new BehaviorSubject<SpaceDomain.PermissionsSearchResult[]>([]);
  public searchResults$: Observable<SpaceDomain.PermissionsSearchResult[]>;
  public selectedResults$: Observable<SpaceDomain.PermissionsSearchResult[]>;
  public queryParams$: BehaviorSubject<SearchQueryParams> = new BehaviorSubject(null);

  constructor(
    private bugsnagService: BugsnagService,
    private spacesClientService: SpacesClientService
  ) {
    this.selectedResults$ = this.selectedResultsStream.asObservable();
    this.searchResults$ = this.queryParams$.pipe(
      debounceTime(400),
      switchMap(({ orgName, spaceId, query }: SearchQueryParams) => this.memberSearch(orgName, spaceId, query))
    );
  }

  memberSearch(orgName: string, spaceToken: string, query: string): Observable<SpaceDomain.PermissionsSearchResult[]> {
    return this.spacesClientService.searchUnentitledSpaceMembers(orgName, spaceToken, query).pipe(
      catchError(() => {
        return of([]);
      })
    );
  }

  addSelectedResult(selectedResult: SpaceDomain.PermissionsSearchResult) {
    selectedResult.action = CollectionAccessLevel.View;
    this.selectedResultsStream.next([...this.selectedResultsStream.getValue(), selectedResult]);
  }

  clearSelectedResults() {
    this.selectedResultsStream.next([]);
  }

  removeSelectedResult(selectedResult: SpaceDomain.PermissionsSearchResult) {
    this.selectedResultsStream.next(
      this.selectedResultsStream.getValue().filter((result) => result.token !== selectedResult.token)
    );
  }

  updateSelectedResultAction(action: CollectionAccessLevel, updatingEntitlement: SpaceDomain.PermissionsSearchResult) {
    const currentSelected = this.selectedResultsStream.getValue();
    const newSelected = currentSelected.map((entitlement) => {
      if (entitlement.token === updatingEntitlement.token) {
        const updated = entitlement;
        updated.action = action;
        return updated;
      }
      return entitlement;
    });
    this.selectedResultsStream.next(newSelected);
  }

  updateQuerySearchParams(newData: SearchQueryParams) {
    this.queryParams$.next(newData);
  }

  createEntitlement(accountId: string, spaceToken: string, selectedResult: SpaceDomain.PermissionsSearchResult) {
    return this.spacesClientService
      .createSpaceEntitlement(
        accountId,
        spaceToken,
        selectedResult.action || CollectionAccessLevel.View,
        selectedResult.token,
        selectedResult.type
      )
      .pipe(tap(() => this.removeSelectedResult(selectedResult)));
  }

  createEntitlements(accountId: string, spaceToken: string, selectedResults: SpaceDomain.PermissionsSearchResult[]) {
    let hasFailures = false;

    return of(selectedResults).pipe(
      mergeMap(
        (selectedResults) =>
          forkJoin(
            selectedResults.map((selectedResult) =>
              this.createEntitlement(accountId, spaceToken, selectedResult).pipe(
                catchError((e) => {
                  // track the unsuccessful attempts to handle them once all are finished
                  hasFailures = true;

                  this.logFailure(e, accountId, spaceToken, selectedResult);

                  // fulfill the contract by returning the error wrapped in an observable
                  return of(e);
                })
              )
            )
          ),
        3
      ),
      tap(() => {
        if (hasFailures) {
          throw new Error('Some entitlements could not be created.');
        }
      })
    );
  }

  logFailure(err: Error, accountId: string, spaceToken: string, selectedResult: SpaceDomain.PermissionsSearchResult) {
    this.bugsnagService.notify(err, (event: any) => {
      event.addMetaData('Selection', { accountId, spaceToken, selectedResult });
    });
  }
}
